import React, { useEffect, useMemo, useState } from "react";
import { Container, Button, Form, Row, Col, Spinner } from "react-bootstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import { toast } from "react-toastify";

import { UPDATE_TENANT_MUTATION } from "../../../graphql/mutations";
import { GET_REGIONS_BY_TENANT_ID, GET_SUB_REGIONS_BY_TENANT_ID } from "../../../graphql/queries";
import { useAppContext } from "../../../context/appContext";
import FeatureAssignmentSection from "../../FeatureConfig/FeatureAssignmentSection";
import CatalogRegions from "./sections/CatalogRegions";
import CatalogSubRegions from "./sections/CatalogSubRegions";

let clientKeySeq = 1;
const nextClientKey = () => `ck-${clientKeySeq++}`;

const normalizeCatalogName = (name) => (name || "").trim().toLowerCase();

const isCatalogNameTaken = (items, name, excludeClientKey) => {
  const needle = normalizeCatalogName(name);
  if (!needle) return false;
  return items.some((item) => item.clientKey !== excludeClientKey && normalizeCatalogName(item.name) === needle);
};

const EditTenantPage = ({ subscriptions = [], env, handleEnvChange, tenants = [], viewOnly = false }) => {
  const { confirmNavigation } = useAppContext() || {};
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const tenantFromState = location.state?.tenant;

  const tenant = useMemo(() => {
    if (tenantFromState?.id === tenantId) return tenantFromState;
    return (tenants || []).find((t) => t.id === tenantId) || tenantFromState || null;
  }, [tenantFromState, tenants, tenantId]);

  const [formData, setFormData] = useState({
    name: "",
    alias: "",
    description: "",
    subscription: "",
    defaultUnit: "metric"
  });
  const [isDuplicatePublicId, setIsDuplicatePublicId] = useState(false);
  const [regions, setRegions] = useState([]);
  const [subRegions, setSubRegions] = useState([]);
  const [savingTenant, setSavingTenant] = useState(false);

  const {
    loading: regionsLoading,
    data: regionsData,
    error: regionsError
  } = useQuery(GET_REGIONS_BY_TENANT_ID, {
    variables: { tenantId },
    skip: !tenantId
  });

  const {
    loading: subRegionsLoading,
    data: subRegionsData,
    error: subRegionsError
  } = useQuery(GET_SUB_REGIONS_BY_TENANT_ID, {
    variables: { tenantId },
    skip: !tenantId
  });

  const [updateTenant] = useMutation(UPDATE_TENANT_MUTATION);

  useEffect(() => {
    if (!tenant) return;
    setFormData({
      name: tenant.name || "",
      alias: tenant.alias || "",
      description: tenant.description || "",
      subscription: tenant.subscription?.id || "",
      defaultUnit: tenant.defaultUnit || "metric"
    });
  }, [tenant]);

  useEffect(() => {
    if (regionsData?.getRegionsByTenantId) {
      setRegions(
        regionsData.getRegionsByTenantId.map((region) => ({
          clientKey: nextClientKey(),
          id: region.id,
          name: region.name || "",
          description: region.description || ""
        }))
      );
    }
  }, [regionsData]);

  useEffect(() => {
    if (subRegionsData?.getSubRegionsByTenantId) {
      setSubRegions(
        subRegionsData.getSubRegionsByTenantId.map((item) => ({
          clientKey: nextClientKey(),
          id: item.id,
          name: item.name || "",
          description: item.description || ""
        }))
      );
    }
  }, [subRegionsData]);

  useEffect(() => {
    if (regionsError) toast.error("Failed to load regions.");
  }, [regionsError]);

  useEffect(() => {
    if (subRegionsError) toast.error("Failed to load sub-regions.");
  }, [subRegionsError]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "name") {
      const duplicate = (tenants || []).some((t) => t.id !== tenantId && t.name === value);
      setIsDuplicatePublicId(duplicate);
    }
  };

  const addRegion = () => {
    if (viewOnly) return;
    setRegions((prev) => [{ clientKey: nextClientKey(), name: "", description: "" }, ...prev]);
  };

  const changeRegionField = (clientKey, field, value) => {
    setRegions((prev) => prev.map((region) => (region.clientKey === clientKey ? { ...region, [field]: value } : region)));
  };

  const removeRegion = (clientKey) => {
    setRegions((prev) => prev.filter((region) => region.clientKey !== clientKey));
  };

  const addSubRegion = () => {
    if (viewOnly) return;
    setSubRegions((prev) => [{ clientKey: nextClientKey(), name: "", description: "" }, ...prev]);
  };

  const changeSubRegionField = (clientKey, field, value) => {
    setSubRegions((prev) => prev.map((item) => (item.clientKey === clientKey ? { ...item, [field]: value } : item)));
  };

  const removeSubRegion = (clientKey) => {
    setSubRegions((prev) => prev.filter((item) => item.clientKey !== clientKey));
  };

  const findCatalogListError = (items, label) => {
    const blankIndex = items.findIndex((item) => !item.name.trim());
    if (blankIndex !== -1) {
      return `${label} #${blankIndex + 1} is missing a name.`;
    }
    const duplicate = items.find((item) => isCatalogNameTaken(items, item.name, item.clientKey));
    if (duplicate) {
      return `${label} name "${duplicate.name.trim()}" is used more than once.`;
    }
    return null;
  };

  const handleSaveTenant = async (e) => {
    e.preventDefault();
    if (viewOnly || isDuplicatePublicId) return;

    const regionError = findCatalogListError(regions, "Region");
    if (regionError) {
      toast.error(regionError);
      return;
    }

    const subRegionError = findCatalogListError(subRegions, "Sub-Region");
    if (subRegionError) {
      toast.error(subRegionError);
      return;
    }
    if (confirmNavigation && !(await confirmNavigation("save-tenant"))) {
      return;
    }
    setSavingTenant(true);
    try {
      const { data: tenantData } = await updateTenant({
        variables: {
          tenantId,
          tenantInfoInput: {
            name: formData.name,
            alias: formData.alias === "" ? null : formData.alias,
            description: formData.description === "" ? null : formData.description,
            defaultUnit: formData.defaultUnit,
            subscriptionId: formData.subscription === "" ? null : formData.subscription,
            regions: regions.map((region) => ({
              ...(region.id ? { id: region.id } : {}),
              name: region.name.trim(),
              description: region.description.trim() === "" ? null : region.description.trim()
            })),
            subRegions: subRegions.map((item) => ({
              ...(item.id ? { id: item.id } : {}),
              name: item.name.trim(),
              description: item.description.trim() === "" ? null : item.description.trim()
            }))
          }
        }
      });
      toast.success(`Tenant ${tenantData?.updateTenant || tenantId} saved.`);
      if (handleEnvChange) handleEnvChange(env, tenantId, formData.name);
      navigate("/tenants");
    } catch (err) {
      toast.error(err?.message || "Failed to save tenant.");
    } finally {
      setSavingTenant(false);
    }
  };

  if (!tenantId) {
    return (
      <Container fluid className="mt-3">
        <p>Missing tenant id.</p>
        <Button onClick={() => navigate("/tenants")}>Back</Button>
      </Container>
    );
  }

  if (!tenant) {
    return (
      <Container fluid className="mt-3">
        <p>Tenant not found in the current list. Open Edit from the Tenants page.</p>
        <Button onClick={() => navigate("/tenants")}>Back to Tenants</Button>
      </Container>
    );
  }

  const catalogsLoading = regionsLoading || subRegionsLoading;

  return (
    <Container fluid className="mt-2">
      <h4 className="mb-2">{viewOnly ? "View Tenant" : "Edit Tenant"}</h4>
      {catalogsLoading && (
        <div className="mb-2">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading catalogs…
        </div>
      )}
      <Form onSubmit={handleSaveTenant}>
        <Row className="mb-3">
          <Col md={3}>
            <Form.Group className="mb-2" controlId="formName">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                disabled={viewOnly}
                isInvalid={isDuplicatePublicId}
              />
              <Form.Control.Feedback type="invalid">A tenant with the same name already exists</Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group className="mb-2" controlId="formAlias">
              <Form.Label>Alias</Form.Label>
              <Form.Control type="text" name="alias" value={formData.alias} onChange={handleInputChange} required disabled={viewOnly} />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group className="mb-2" controlId="formSubscription">
              <Form.Label>Subscription</Form.Label>
              <Form.Select name="subscription" value={formData.subscription} onChange={handleInputChange} disabled={viewOnly}>
                <option value=""></option>
                {subscriptions.map((subscription) => (
                  <option key={subscription.id} value={subscription.id}>
                    {subscription.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group controlId="defaultUnit" className="mb-2">
              <Form.Label>Default Unit</Form.Label>
              <Form.Select name="defaultUnit" value={formData.defaultUnit} onChange={handleInputChange} required disabled={viewOnly}>
                <option value="metric">Metric</option>
                <option value="imperial">Imperial</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={12}>
            <Form.Group className="mb-2" controlId="formDescription">
              <Form.Label>Description</Form.Label>
              <Form.Control type="text" name="description" value={formData.description} onChange={handleInputChange} disabled={viewOnly} />
            </Form.Group>
          </Col>
        </Row>

        <CatalogRegions
          regions={regions}
          onChange={changeRegionField}
          onAdd={addRegion}
          onRemove={removeRegion}
          viewOnly={viewOnly}
          loading={catalogsLoading}
        />

        <CatalogSubRegions
          subRegions={subRegions}
          onChange={changeSubRegionField}
          onAdd={addSubRegion}
          onRemove={removeSubRegion}
          viewOnly={viewOnly}
          loading={catalogsLoading}
        />

        <div className="d-flex gap-2 mb-3">
          <Button
            variant="secondary"
            type="button"
            onClick={async () => {
              if (!confirmNavigation || (await confirmNavigation())) {
                navigate("/tenants");
              }
            }}
          >
            Cancel
          </Button>
          {!viewOnly && (
            <Button variant="primary" type="submit" disabled={savingTenant || isDuplicatePublicId}>
              {savingTenant ? "Saving…" : "Save Tenant"}
            </Button>
          )}
        </div>
      </Form>

      <FeatureAssignmentSection env={env} tenantId={tenantId} tenantName={tenant.name} viewOnly={viewOnly} />
    </Container>
  );
};

export default EditTenantPage;
